import type { LeadKind } from "../types";

/** The copy key a product is named by on the page: the card offering a piece,
 *  "Order them together" and the strip of their other previews all say it the
 *  same way. The head keychain (a person's head, sold all year) is named apart
 *  from the pet's, as the website's form names its card ("MiniME keychain"):
 *  on a page with both, two pieces called "Keychain" could only be told apart
 *  by their photos. */
export const KIND_KEY: Record<LeadKind, string> = {
  minime: "kindMinime",
  pet_keychain: "kindKeychain",
  duo: "kindDuo",
  head_keychain: "kindHeadKeychain",
};

/** The size a product's sheet opens on: 8 cm for the figurines, as the
 *  sheet always has (the middle size, the one the website calls the most
 *  popular choice); the smallest for the keychains, which is the one nearly
 *  every keychain buyer takes. Null opens on the smallest. The labels are
 *  the price grid's own (functions/src/lib/templates.seed.json), the same in
 *  every language. */
export const RECOMMENDED_SIZE: Record<LeadKind, string | null> = {
  minime: "8 cm",
  duo: "8 cm",
  pet_keychain: null,
  head_keychain: null,
};

/** The size, if any, whose row and chip carry the "Recommended" tag. None
 *  today: the website makes no such recommendation (it says every size keeps
 *  the same facial detail), and a tag the business has not decided on would
 *  be an invented claim. When the owner recommends a size, name it here and
 *  the tag follows, in every language (copy.json "recommended"). */
export const TAGGED_SIZE: Record<LeadKind, string | null> = {
  minime: null,
  duo: null,
  pet_keychain: null,
  head_keychain: null,
};

/** The size to open on: the product's recommended one where it is offered,
 *  the first offered otherwise. */
export function openingSize(sizes: { size: string }[], kind?: LeadKind | null): string {
  const wanted = kind ? RECOMMENDED_SIZE[kind] : null;
  return (sizes.find((s) => s.size === wanted) ?? sizes[0])?.size ?? "";
}
